import { FC, useEffect } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import { useTranslation } from '@eneco/i18n';
import { NotificationBoxProps } from '@eneco/sparky/types';
import { useTracking } from '@eneco/tracking';

import { useMeterReadingsFlow } from '../../../state/MeterReadingsFlow.machine';
import { CONFIRMATION_STATES } from '../../../state/MeterReadingsFlow.types';
import { Steps } from '../../../tracking';

const notifications: Partial<
  Record<
    CONFIRMATION_STATES['CONFIRMATION'],
    { notificationType: NotificationBoxProps['variant']; title: string; text: string; funnelComplete?: boolean }
  >
> = {
  ERROR_NETWORK: {
    notificationType: 'warning',
    title: 'saveErrorNetworkTitle',
    text: 'saveErrorNetworkText',
  },
  ERROR_YEARNOTE: {
    notificationType: 'info',
    title: 'saveErrorYearNoteTitle',
    text: 'saveErrorYearNoteText',
    funnelComplete: true,
  },
  ERROR_SWITCH: {
    notificationType: 'info',
    title: 'saveErrorSwitchTitle',
    text: 'saveErrorSwitchText',
    funnelComplete: true,
  },
  ERROR_ENDNOTE: {
    notificationType: 'info',
    title: 'saveErrorEndNoteTitle',
    text: 'saveErrorEndNoteText',
    funnelComplete: true,
  },
  ERROR_UNKNOWN: {
    notificationType: 'warning',
    title: 'saveErrorUnknownTitle',
    text: 'saveErrorUnknownText',
  },
};

export const SaveReadingNotification: FC = () => {
  const { t } = useTranslation();
  const { state } = useMeterReadingsFlow();
  const { trackFunnelCompleted } = useTracking();

  const { value } = state;
  const stateValue = (value as CONFIRMATION_STATES).CONFIRMATION;

  const notification = notifications[stateValue];
  useEffect(() => {
    if (notification?.funnelComplete) {
      trackFunnelCompleted({
        step: `${Steps['CONFIRMATION']} - ${notification.title} - ${stateValue}`,
        funnel: state.context?.funnelName,
      });
    }
  }, [state.context.funnelName, trackFunnelCompleted, notification, stateValue]);

  if (!notification) return null;

  return (
    <TrackedNotificationBox
      title={t(notification.title)}
      label={`${t(notification.title)} - ${stateValue}`}
      text={<RichText html={t(notification.text)} />}
      isAlert={true}
      variant={notification.notificationType}
    />
  );
};
